import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Grid, IconButton, Paper, Tabs, Tab, Typography, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Cell, PieChart, Pie } from 'recharts';
import { Refresh } from '@mui/icons-material';
import inventoryCommandService from '../../services/inventoryCommandService';
import { formatIndianNumber } from '../../utils/currency';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import { PageHeader, TableSurface, headerCellSx, bodyRowSx, TypePill, ErrorState, TableEmptyState } from '../../components/common/pageKit';

// ---------------------------------------------------------------------------
// Types — a defensive read of inventoryCommandService.getAnalytics() on the
// API. The pass-through engines (getStockAgeing / getDeadStock /
// getAbcClassification) are stable today, but this page never assumes a key
// exists before checking it.
// ---------------------------------------------------------------------------

interface AgeingItem { productId: number; productName: string; productCode: string; quantity: number; value: number }
type AgeingBuckets = Record<string, AgeingItem[]>;

interface DeadStockItem {
  productId: number; productName: string; productCode: string; warehouseId?: number;
  currentStock: number; stockValue: number; lastIssued: string | null; daysSinceLastIssue: number | null;
}

interface AbcItem {
  productId: number; productName: string; productCode: string; stockValue: number; cumulativePct: number; category: 'A' | 'B' | 'C';
}

interface AnalyticsData { ageing: AgeingBuckets; deadStock: DeadStockItem[]; abc: AbcItem[] }

const AGEING_ORDER = ['0-30', '31-60', '61-90', '90+'];
const AGEING_LABEL: Record<string, string> = { '0-30': '0–30 days', '31-60': '31–60 days', '61-90': '61–90 days', '90+': '90+ days' };

const deadStockAction = (days: number | null): string => {
  if (days == null) return 'Investigate — no movement ever recorded';
  if (days >= 180) return 'Consider write-off / liquidation';
  if (days >= 90) return 'Review demand plan / consider markdown';
  return 'Monitor';
};

// ---------------------------------------------------------------------------
// Section shell — matches the bordered-card treatment used across analytics
// pages (see ProcurementAnalyticsPage). Not lifted into pageKit since only
// this page currently needs a titled chart card.
// ---------------------------------------------------------------------------

const SectionCard: React.FC<{ title: string; subtitle?: string; children: React.ReactNode; sx?: any }> = ({ title, subtitle, children, sx }) => (
  <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2.5, height: '100%', ...sx }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: subtitle ? 0.25 : 1.5 }}>{title}</Typography>
    {subtitle && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>{subtitle}</Typography>}
    {children}
  </Paper>
);

const InventoryAnalyticsPage: React.FC = () => {
  const t = useChartTokens();
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'ageing' | 'deadStock' | 'abc'>('ageing');
  const [ageingBucket, setAgeingBucket] = useState<string>('90+');
  const [abcFilter, setAbcFilter] = useState<'A' | 'B' | 'C' | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    inventoryCommandService
      .analytics()
      .then((res: any) => {
        const ageing: AgeingBuckets = res && typeof res.ageing === 'object' && res.ageing !== null ? res.ageing : {};
        const deadStock: DeadStockItem[] = Array.isArray(res?.deadStock) ? res.deadStock : [];
        const abc: AbcItem[] = Array.isArray(res?.abc) ? res.abc : [];
        setData({ ageing, deadStock, abc });
      })
      .catch((e: any) => setError(e?.message || 'Failed to load inventory analytics'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ageingSeries = useMemo(() => {
    const buckets = data?.ageing || {};
    const keys = AGEING_ORDER.filter((k) => k in buckets).length ? AGEING_ORDER : Object.keys(buckets);
    return keys.map((key) => {
      const items = Array.isArray(buckets[key]) ? buckets[key] : [];
      return {
        key,
        label: AGEING_LABEL[key] || key,
        count: items.length,
        value: items.reduce((s, it) => s + (Number(it.value) || 0), 0),
        items,
      };
    });
  }, [data]);

  const ageingActiveItems = useMemo(
    () => (ageingSeries.find((b) => b.key === ageingBucket)?.items || []).slice().sort((a, b2) => b2.value - a.value),
    [ageingSeries, ageingBucket]
  );

  const deadStock = data?.deadStock || [];
  const totalDeadStockValue = useMemo(() => deadStock.reduce((s, it) => s + (Number(it.stockValue) || 0), 0), [deadStock]);

  const abc = data?.abc || [];
  const abcClasses = useMemo(() => {
    const byClass: Record<'A' | 'B' | 'C', { count: number; value: number }> = { A: { count: 0, value: 0 }, B: { count: 0, value: 0 }, C: { count: 0, value: 0 } };
    for (const it of abc) {
      const c = byClass[it.category] || (byClass[it.category] = { count: 0, value: 0 });
      c.count += 1;
      c.value += Number(it.stockValue) || 0;
    }
    return byClass;
  }, [abc]);
  const abcTotalValue = abcClasses.A.value + abcClasses.B.value + abcClasses.C.value;
  const abcPieData = (['A', 'B', 'C'] as const).map((cat) => ({ name: cat, value: abcClasses[cat].value }));
  const ABC_COLOR: Record<'A' | 'B' | 'C', string> = { A: t.series(0), B: t.series(1), C: t.series(2) };
  const abcFilteredItems = useMemo(() => (abcFilter ? abc.filter((it) => it.category === abcFilter) : abc), [abc, abcFilter]);

  return (
    <Box>
      <PageHeader
        title="Inventory Analytics"
        subtitle="Stock ageing, dead stock and ABC value classification, computed deterministically from cost layers and movement history"
        actions={
          <IconButton onClick={load} disabled={loading}>
            <Refresh />
          </IconButton>
        }
      />

      {loading && data === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : (
        <>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2.5, minHeight: 36 }}>
            <Tab value="ageing" label="Ageing" sx={{ minHeight: 36, textTransform: 'none' }} />
            <Tab value="deadStock" label={`Dead Stock (${deadStock.length})`} sx={{ minHeight: 36, textTransform: 'none' }} />
            <Tab value="abc" label="ABC Classification" sx={{ minHeight: 36, textTransform: 'none' }} />
          </Tabs>

          {tab === 'ageing' && (
            <Grid container spacing={2.5}>
              <Grid size={12}>
                <SectionCard title="Stock Value by Age" subtitle="Value of remaining cost layers, bucketed by days since receipt — click a bar or chip to inspect it below">
                  {ageingSeries.every((b) => b.count === 0) ? (
                    <TableEmptyState title="No ageing data" hint="No open cost layers were found." />
                  ) : (
                    <Box sx={{ height: 240 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={ageingSeries} margin={{ top: 12, right: 8, left: -6, bottom: 4 }}>
                          <CartesianGrid {...gridProps(t)} vertical={false} />
                          <XAxis dataKey="label" {...axisProps(t)} />
                          <YAxis {...axisProps(t)} width={64} tickFormatter={(v: number) => formatIndianNumber(v)} />
                          <RTooltip {...tooltipStyle(t)} formatter={(v: number, _n, p: any) => [`${formatIndianNumber(v)} · ${p?.payload?.count ?? 0} item(s)`, 'Value']} />
                          <Bar dataKey="value" radius={[3, 3, 0, 0]} maxBarSize={56} onClick={(d: any) => d?.key && setAgeingBucket(d.key)} style={{ cursor: 'pointer' }}>
                            {ageingSeries.map((s, i) => {
                              const severityColor = i === 0 ? t.status.good : i === 1 ? t.status.warning : i === 2 ? t.status.serious : t.status.critical;
                              return <Cell key={s.key} fill={severityColor} opacity={s.key === ageingBucket ? 1 : 0.55} />;
                            })}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
                    {ageingSeries.map((s, i) => {
                      const severityColor = i === 0 ? t.status.good : i === 1 ? t.status.warning : i === 2 ? t.status.serious : t.status.critical;
                      const active = s.key === ageingBucket;
                      return (
                        <Box
                          key={s.key}
                          onClick={() => setAgeingBucket(s.key)}
                          sx={{
                            px: 1.25, py: 0.625, borderRadius: 2, cursor: 'pointer',
                            border: active ? `1.5px solid ${severityColor}` : '1px solid', borderColor: active ? severityColor : 'divider',
                            bgcolor: active ? `${severityColor}14` : 'transparent',
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>{s.label}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>{s.count} · {formatIndianNumber(s.value)}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </SectionCard>
              </Grid>

              <Grid size={12}>
                <TableSurface>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          {['Item', 'Qty', 'Value'].map((h, i) => (
                            <TableCell key={h} sx={headerCellSx} align={i > 0 ? 'right' : 'left'}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ageingActiveItems.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ border: 0 }}>
                              <TableEmptyState title="No items in this age bucket" />
                            </TableCell>
                          </TableRow>
                        ) : (
                          ageingActiveItems.map((it) => (
                            <TableRow key={it.productId} hover sx={bodyRowSx}>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{it.productName}</Typography>
                                <Typography variant="caption" color="text.secondary">{it.productCode}</Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{it.quantity}</TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{formatIndianNumber(it.value)}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </TableSurface>
              </Grid>
            </Grid>
          )}

          {tab === 'deadStock' && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                <b>{deadStock.length}</b> item{deadStock.length === 1 ? '' : 's'} with no issue in the lookback window · <b>{formatIndianNumber(totalDeadStockValue)}</b> tied up
              </Typography>
              <TableSurface>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {['Item', 'Qty', 'Value', 'Days Since Last Movement', 'Recommended Action'].map((h, i) => (
                          <TableCell key={h} sx={headerCellSx} align={i === 1 || i === 2 || i === 3 ? 'right' : 'left'}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deadStock.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ border: 0 }}>
                            <TableEmptyState title="No dead stock" hint="Every item has moved recently." />
                          </TableCell>
                        </TableRow>
                      ) : (
                        deadStock.map((it) => (
                          <TableRow key={`${it.productId}-${it.warehouseId ?? 0}`} hover sx={bodyRowSx}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>{it.productName}</Typography>
                              <Typography variant="caption" color="text.secondary">{it.productCode}</Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{it.currentStock}</TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{formatIndianNumber(it.stockValue)}</TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{it.daysSinceLastIssue != null ? `${it.daysSinceLastIssue}d` : 'Never'}</TableCell>
                            <TableCell>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: (it.daysSinceLastIssue == null || it.daysSinceLastIssue >= 180) ? 'error.main' : 'warning.main' }}>
                                {deadStockAction(it.daysSinceLastIssue)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TableSurface>
            </Box>
          )}

          {tab === 'abc' && (
            <Grid container spacing={2.5}>
              <Grid size={{ xs: 12, md: 4 }}>
                <SectionCard title="Value Concentration" subtitle="A = top 80% of value, B = next 15%, C = remainder">
                  {abc.length === 0 ? (
                    <TableEmptyState title="No valued stock" />
                  ) : (
                    <>
                      <Box sx={{ height: 200, position: 'relative' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={abcPieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={80} paddingAngle={2} strokeWidth={2} stroke={t.surface}>
                              {abcPieData.map((d) => (
                                <Cell key={d.name} fill={ABC_COLOR[d.name as 'A' | 'B' | 'C']} opacity={abcFilter && abcFilter !== d.name ? 0.35 : 1} />
                              ))}
                            </Pie>
                            <RTooltip {...tooltipStyle(t)} formatter={(v: number, n: any) => [formatIndianNumber(v), `Class ${n}`]} />
                          </PieChart>
                        </ResponsiveContainer>
                        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>{formatIndianNumber(abcTotalValue)}</Typography>
                          <Typography variant="caption" color="text.secondary">total value</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                        {(['A', 'B', 'C'] as const).map((cat) => {
                          const c = abcClasses[cat];
                          const pct = abcTotalValue > 0 ? (c.value / abcTotalValue) * 100 : 0;
                          const active = abcFilter === cat;
                          return (
                            <Box
                              key={cat}
                              onClick={() => setAbcFilter(active ? null : cat)}
                              sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', p: 0.75, borderRadius: 1.5, bgcolor: active ? `${ABC_COLOR[cat]}14` : 'transparent' }}
                            >
                              <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: ABC_COLOR[cat], flexShrink: 0 }} />
                              <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 60 }}>Class {cat}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{c.count} item{c.count === 1 ? '' : 's'}</Typography>
                              <Typography variant="caption" sx={{ fontWeight: 700 }}>{formatIndianNumber(c.value)} ({pct.toFixed(0)}%)</Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  )}
                </SectionCard>
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                <TableSurface>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          {['Item', 'Class', 'Value', 'Cumulative %'].map((h, i) => (
                            <TableCell key={h} sx={headerCellSx} align={i >= 2 ? 'right' : 'left'}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {abcFilteredItems.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} sx={{ border: 0 }}>
                              <TableEmptyState title="No items" />
                            </TableCell>
                          </TableRow>
                        ) : (
                          abcFilteredItems.map((it) => (
                            <TableRow key={it.productId} hover sx={bodyRowSx}>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{it.productName}</Typography>
                                <Typography variant="caption" color="text.secondary">{it.productCode}</Typography>
                              </TableCell>
                              <TableCell>
                                <TypePill label={`Class ${it.category}`} tint={ABC_COLOR[it.category]} />
                              </TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{formatIndianNumber(it.stockValue)}</TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{it.cumulativePct.toFixed(1)}%</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </TableSurface>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Box>
  );
};

export default InventoryAnalyticsPage;
